import { Card, Icon } from '@navinc/base-react-components/wayfinder'
import { getBlogArticleParentPath } from '@navinc/common'
import { LoaderFunctionArgs } from 'react-router'
import { type MetaFunction, useLoaderData, useRouteLoaderData } from 'react-router'

import { Accordion } from '@/app/_components/Accordion/index.jsx'
import { ArticleTableOfContents } from '@/app/_components/ArticleTableOfContents/index.jsx'
import { AuthorBadge } from '@/app/_components/AuthorBadge/index.jsx'
import { AuthorCard } from '@/app/_components/AuthorCard/index.jsx'
import { Callout } from '@/app/_components/Callout/index.jsx'
import { Disclaimer } from '@/app/_components/Disclaimer/index.jsx'
import { Footer } from '@/app/_components/Footer/index.jsx'
import { Header } from '@/app/_components/Header/index.js'
import { getMediaSizeUrlWithFallback } from '@/app/_components/Media/utils.js'
import { RelatedArticles } from '@/app/_components/RelatedArticles/index.jsx'
import { Children } from '@/app/_components/RichText/serialize.jsx'
import { RouteBody } from '@/app/_components/RouteBody/index.jsx'
import { ShareButtons } from '@/app/_components/ShareButtons/index.jsx'
import { CmsMediaFragment } from '@/app/_graphql/media.js'
import { CmsArticle_ResourceCategory_Input as CmsArticleResourceCategoryInput } from '@/app/lib/cms/generated/graphql.js'

import { pageCacheKey, withCache } from '../../.server/cache.js'
import { Blocks } from '../../_blocks/Blocks.jsx'
import { FormatDate, longDateOnlyFormatter } from '../../_components/FormatDate/index.jsx'
import { Gutter } from '../../_components/Gutter/index.jsx'
import { OffersProvider } from '../../_components/OffersProvider/offers-provider.jsx'
import { LexicalRichText, LexicalRichTextContent } from '../../_components/RichText/lexical.jsx'
import { InteractionContextProvider } from '../../_providers/InteractionContextProvider/InteractionContextProvider.jsx'
import { isPreviewMode } from '../../_utilities/preview-mode.server.js'
import { getFragmentData } from '../../lib/cms/index.js'
import { captureError } from '../../lib/error-reporting.js'
import { promiseValueErrorTuple } from '../../lib/promise-value-error-tuple.js'
import { toCanonicalUrl } from '../../lib/to-canonical-url.js'
import { urlHasNoCache } from '../../lib/url-has-no-cache.js'
import { wrapError } from '../../lib/wrap-error.js'
import { loader as rootLoader } from '../../root.jsx'
import { ErrorPage } from '../error-page.jsx'
import { fetchArticle } from './page-service.js'

const getReadTimeEstimate = (readTimeEstimateMinutes: number | undefined | null) => {
  return readTimeEstimateMinutes ? `${readTimeEstimateMinutes} min read` : null
}

export const loader = async ({
  params,
  request,
  resourceCategoryParent,
}: LoaderFunctionArgs & { resourceCategoryParent?: CmsArticleResourceCategoryInput | null }) => {
  const { slug } = params

  const preview = await isPreviewMode(request)
  const canonicalUrl = toCanonicalUrl(request.url)
  const disableCache = preview || urlHasNoCache(request.url)

  const [article, pageError] = await promiseValueErrorTuple(
    withCache(
      pageCacheKey(canonicalUrl),
      () => fetchArticle({ request, slug, draft: preview, canonicalUrl, disableCache, resourceCategoryParent }),
      {
        disable: disableCache,
      },
    ),
  )

  if (!pageError && !article) {
    throw new Response('Not Found', { status: 404 })
  }

  if (pageError) {
    captureError(wrapError(`articles.(${slug}) fetch page error`, pageError))
  }

  return {
    canonicalUrl,
    slug,
    article,
    pageError,
  }
}

export default function RootSlug() {
  const data = useLoaderData<typeof loader>()
  const { header, footer } = useRouteLoaderData<typeof rootLoader>('root')!

  const { article, pageError } = data

  if (pageError) {
    return <ErrorPage />
  }

  const {
    content,
    customHeader,
    customFooter,
    richText,
    title,
    publishedAt,
    authors,
    readTimeEstimate,
    tableOfContents,
    relatedArticles,
    summary,
    navPrimeCallout,
    advertiserDisclosure,
    editorialNote,
    offers,
    image,
  } = article!

  const readTimeEstimateValue = getReadTimeEstimate(readTimeEstimate)
  const featuredImageUrl = image ? getMediaSizeUrlWithFallback(image, 'feature') : null
  const featuredImageAlt = getFragmentData(CmsMediaFragment, image)?.alt || ''

  // sidebar content will be shown on the sidebar on desktop, but shown below the article body on mobile
  const sidebarContent = (
    <div className="py-400">
      {navPrimeCallout ? <Callout {...navPrimeCallout.callout} /> : null}
      <div className="py-400">
        <ShareButtons title={title ?? ''} url={data.canonicalUrl.toString()} />
      </div>
    </div>
  )

  return (
    <>
      <InteractionContextProvider elementLocation="header">
        <Header header={header} customHeader={customHeader} />
      </InteractionContextProvider>
      <OffersProvider offers={offers ?? []}>
        <RouteBody>
          <div className="flex w-full flex-col bg-primary pt-800 md:min-h-[544px] md:pt-[60px]">
            <Gutter className="grid grow grid-cols-1 gap-600 px-200 py-300 md:grid-cols-2 md:py-[72px]">
              <div className="col-span-1 flex flex-col text-inverseOnSurface md:justify-center">
                <h1 className="display2-emphasized md:display1-emphasized col-span-1 mb-400">{title}</h1>

                {authors && (
                  <div className="flex flex-wrap items-center gap-200">
                    {authors.map((author, authorIndex) => {
                      const authorImage = getFragmentData(CmsMediaFragment, author.media)
                      return (
                        <AuthorBadge
                          key={authorIndex}
                          authorName={author.name}
                          authorImageUrl={authorImage?.url}
                          authorDescription={author.description}
                        />
                      )
                    })}
                  </div>
                )}

                <div className="body2 my-300 flex gap-200">
                  {publishedAt && (
                    <>
                      <span>
                        <FormatDate date={new Date(publishedAt)} formatter={longDateOnlyFormatter} />
                      </span>
                    </>
                  )}
                  {readTimeEstimateValue && (
                    <>
                      |<span>{readTimeEstimateValue}</span>
                    </>
                  )}
                </div>
              </div>
              {featuredImageUrl && (
                <div className="relative aspect-[7/4] w-full overflow-hidden rounded-200">
                  <img alt={featuredImageAlt} src={featuredImageUrl} className="size-full object-cover" />
                </div>
              )}
            </Gutter>
          </div>
          <div className="my-300 md:my-[72px]">
            <Gutter className="max-w-[1472px]">
              <div className="Wrapper grid w-full grid-cols-12 gap-300 px-200 desktop:gap-800">
                <aside
                  className="accordion top-[70px] col-span-12 overflow-auto bg-surface md:sticky md:col-span-4"
                  style={{ maxHeight: 'calc(100vh - 60px)' }}
                >
                  {tableOfContents && tableOfContents.length ? <ArticleTableOfContents data={tableOfContents} /> : null}
                  <div className="hidden md:flex md:flex-col">{sidebarContent}</div>
                </aside>

                <div className="Content col-span-12 flex flex-col md:col-span-8">
                  <section className="mb-200">
                    <Card className="p-300">
                      {summary && summary.length ? (
                        <>
                          <h2 className="title1-emphasized mb-200">Summary</h2>
                          <ul className="mb-400 flex flex-col gap-200">
                            {summary.map((summaryItem, summaryItemIndex) => {
                              return (
                                <li key={summaryItemIndex} className="flex gap-100 text-body1">
                                  <span className="inline-flex size-250 items-center pt-50 text-primary">
                                    <Icon name="check_circle" size="medium" />
                                  </span>
                                  {summaryItem.text}
                                </li>
                              )
                            })}
                          </ul>
                        </>
                      ) : null}

                      {editorialNote && (
                        <div className="mb-200">
                          <Disclaimer
                            text={{ richText: editorialNote.richText as Children }}
                            align="left"
                            number={null}
                            includeAsterick={false}
                          />
                        </div>
                      )}

                      {advertiserDisclosure && (
                        <Accordion
                          data={[
                            {
                              title: <span className="text-onSurface">Advertiser Disclosure</span>,
                              valueKey: 'advertiser-disclosure',
                              body: (
                                <div>
                                  <Disclaimer
                                    text={{ richText: advertiserDisclosure.richText as Children }}
                                    align="left"
                                    number={null}
                                    includeAsterick={false}
                                  />
                                </div>
                              ),
                            },
                          ]}
                        />
                      )}
                    </Card>
                  </section>
                  <article>
                    <LexicalRichText content={richText as LexicalRichTextContent} preset="article" />

                    <div className="flex flex-col md:hidden">{sidebarContent}</div>

                    {authors && authors.length ? (
                      <ul className="flex flex-col gap-300 py-300 md:gap-600">
                        {authors.map((author, authorIndex) => {
                          const authorMedia = getFragmentData(CmsMediaFragment, author.media)
                          return (
                            <li key={authorIndex} className="w-full">
                              <AuthorCard
                                authorName={author.name}
                                authorMedia={{
                                  url: getMediaSizeUrlWithFallback(author.media, 'square'),
                                  alt: authorMedia?.alt || '',
                                }}
                                authorDescription={author.description}
                                authorBio={author.shortBio}
                                // allArticlesUrl={`/blog/author/${author.slug}#all-articles`} // Comment this out until sufficient number of author articles are published
                              />
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </article>
                </div>
              </div>
            </Gutter>
          </div>
          {relatedArticles && relatedArticles.length ? (
            <div className="bg-primaryContainer px-200 py-800 text-primaryDim md:py-[72px] desktop:px-0">
              <Gutter>
                <h2 className="display2-emphasized md:display1-emphasized w-full text-center">Read more</h2>
                <div className="py-600 md:py-800">
                  <RelatedArticles
                    docs={relatedArticles.map((relatedArticle) => {
                      return {
                        id: relatedArticle.id,
                        articleUrl: `/${getBlogArticleParentPath(relatedArticle.resourceCategory)}/${relatedArticle.slug}`,
                        mediaUrl: getMediaSizeUrlWithFallback(relatedArticle.image, 'card'),
                        mediaAlt: getFragmentData(CmsMediaFragment, relatedArticle.image)?.alt || '',
                        title: relatedArticle.title,
                        description: relatedArticle.excerpt,
                      }
                    })}
                  />
                </div>
              </Gutter>
            </div>
          ) : null}
          <Blocks data={content} />
        </RouteBody>
      </OffersProvider>
      <InteractionContextProvider elementLocation="footer">
        <Footer footer={footer ?? null} customFooter={customFooter} />
      </InteractionContextProvider>
    </>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data?.article?.compiledMeta ?? []
}
